/**
 * A/B grouping logic for campaign editor (client-side).
 * Groups experiences by matching schedule, location, and device.
 * Only groups with >= 2 experiences are eligible for A/B.
 *
 * ExperienceInput: { id: string, schedule: { start, end } | null, location: { all, countries?, region? }, device: 'all'|'ios'|'android' }
 * expLocations for isGroupSingleCountry: Record<id, { all: boolean, countries: string[], region?: string|null }>
 */

function arraysEqual(a, b) {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

function deviceSet(d) {
  if (d === "all") return new Set(["ios", "android"])
  return new Set([d])
}

function decodeRegion(region) {
  if (!region) return []
  if (typeof region === "string" && region.startsWith("MULTI:")) {
    return region
      .slice(6)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
  }
  return [region]
}

function encodeRegion(ids) {
  const clean = ids.map((s) => String(s).trim()).filter(Boolean)
  if (clean.length === 0) return null
  if (clean.length === 1) return clean[0]
  return `MULTI:${clean.join(",")}`
}

function intersect(a, b) {
  const setB = new Set(b)
  return Array.from(new Set(a.filter((x) => setB.has(x)))).sort()
}

/**
 * @param a - { all?: boolean, countries?: string[], region?: string|null }
 * @param b - same shape
 * @returns overlap location or null
 */
function locationOverlap(a, b) {
  const aAll = a.all === true || !(a.countries && a.countries.length > 0)
  const bAll = b.all === true || !(b.countries && b.countries.length > 0)

  if (aAll && bAll) return { all: true, countries: [], region: null }
  if (aAll && !bAll)
    return { all: false, countries: (b.countries || []).slice(), region: b.region ?? null }
  if (!aAll && bAll)
    return { all: false, countries: (a.countries || []).slice(), region: a.region ?? null }

  const ac = (a.countries || []).slice().sort()
  const bc = (b.countries || []).slice().sort()
  const countries = intersect(ac, bc)
  if (countries.length === 0) return null

  if (countries.length > 1) {
    return { all: false, countries, region: null }
  }

  const country = countries[0]
  const aRegions = decodeRegion(a.region)
  const bRegions = decodeRegion(b.region)
  if (aRegions.length === 0 || bRegions.length === 0) {
    return { all: false, countries: [country], region: null }
  }
  const regions = intersect(aRegions, bRegions)
  if (regions.length === 0) return null
  return { all: false, countries: [country], region: encodeRegion(regions) }
}

/**
 * @param a - { start: string (ISO), end: string (ISO) } | null (campaign schedule)
 * @param b - same
 * @returns overlap schedule or null
 */
function scheduleOverlap(a, b) {
  if (!a && !b) return null
  if (!a && b) return { start: b.start, end: b.end }
  if (a && !b) return { start: a.start, end: a.end }

  const aStart = new Date(a.start).getTime()
  const aEnd = new Date(a.end).getTime()
  const bStart = new Date(b.start).getTime()
  const bEnd = new Date(b.end).getTime()
  const startMs = Math.max(aStart, bStart)
  const endMs = Math.min(aEnd, bEnd)
  if (endMs < startMs) return null
  return {
    start: new Date(startMs).toISOString(),
    end: new Date(endMs).toISOString(),
  }
}

function buildBaseKey(input) {
  const scheduleKey = input.schedule
    ? `${input.schedule.start}..${input.schedule.end}`
    : "CAMPAIGN"
  const loc = input.location
  const countries = (loc.countries ?? []).slice().sort().join(",")
  const region = loc.region ?? ""
  const locationKey = loc.all ? "ALL" : `C:${countries}|R:${region}`
  return `${scheduleKey}__${locationKey}`
}

/**
 * Compute A/B groups: for each (schedule, location) base context, build iOS and Android
 * membership; only output groups with >= 2 experiences. Collapse to "all" device when
 * iOS and Android membership are identical.
 *
 * @param experiences - Array<{ id, schedule, location, device }>
 * @returns Record<groupKey, string[]> where groupKey is like "CAMPAIGN__ALL__all" or "start..end__C:US|R:__ios"
 */
export function computeABGroups(experiences) {
  const membershipByBase = {}

  const ensureBase = (baseKey) => {
    if (!membershipByBase[baseKey]) {
      membershipByBase[baseKey] = { ios: new Set(), android: new Set() }
    }
    return membershipByBase[baseKey]
  }

  for (let i = 0; i < experiences.length; i++) {
    for (let j = i + 1; j < experiences.length; j++) {
      const a = experiences[i]
      const b = experiences[j]

      const overlapSched = scheduleOverlap(a.schedule, b.schedule)
      if (overlapSched === null && a.schedule && b.schedule) continue

      const overlapLoc = locationOverlap(a.location, b.location)
      if (!overlapLoc) continue

      const aDevices = deviceSet(a.device)
      const bDevices = deviceSet(b.device)
      const intersectionDevices = [...aDevices].filter((d) => bDevices.has(d))
      if (intersectionDevices.length === 0) continue

      const baseKey = buildBaseKey({ schedule: overlapSched, location: overlapLoc })
      const bucket = ensureBase(baseKey)

      const addTo = (aud) => {
        bucket[aud].add(a.id)
        bucket[aud].add(b.id)
      }
      if (intersectionDevices.includes("ios")) addTo("ios")
      if (intersectionDevices.includes("android")) addTo("android")
    }
  }

  const out = {}
  for (const baseKey of Object.keys(membershipByBase)) {
    const ios = Array.from(membershipByBase[baseKey].ios).sort()
    const android = Array.from(membershipByBase[baseKey].android).sort()
    const iosOk = ios.length >= 2
    const androidOk = android.length >= 2
    if (!iosOk && !androidOk) continue

    if (iosOk && androidOk && arraysEqual(ios, android)) {
      out[`${baseKey}__all`] = ios
      continue
    }
    if (iosOk) out[`${baseKey}__ios`] = ios
    if (androidOk) out[`${baseKey}__android`] = android
  }
  return out
}

/**
 * Whether all experiences in the group satisfy day/time A/B eligibility:
 * - All target "All Locations" (no geo restriction), OR
 * - All target the same single country.
 *
 * @param ids - experience ids in the group
 * @param expLocations - Record<id, { all: boolean, countries: string[], region?: string|null }>
 * @returns { eligible: boolean, country: string|null }
 */
export function isGroupSingleCountry(ids, expLocations) {
  const rules = ids.map((id) => expLocations[id]).filter(Boolean)
  if (rules.length === 0) return { eligible: false, country: null }
  // All target "All Locations" → eligible (same global targeting)
  if (rules.every((r) => r.all)) return { eligible: true, country: null }
  // Mixed: some "all", some country-specific → not eligible
  if (rules.some((r) => r.all)) return { eligible: false, country: null }
  const countries = ids.map((id) => {
    const rule = expLocations[id]
    if (!rule || !rule.countries || rule.countries.length !== 1) return null
    return rule.countries[0]
  })
  const first = countries[0]
  if (!first) return { eligible: false, country: null }
  const allSame = countries.every((c) => c === first)
  return { eligible: allSame, country: allSame ? first : null }
}

/**
 * Whether all eligible groups (from computeABGroups) satisfy the single-country rule.
 * If any group has multiple or no country targeting, day/time modes are not allowed.
 */
export function allGroupsSingleCountry(abGroups, expLocations) {
  const keys = Object.keys(abGroups)
  if (keys.length === 0) return true
  for (const key of keys) {
    const ids = abGroups[key]
    const { eligible } = isGroupSingleCountry(ids, expLocations)
    if (!eligible) return false
  }
  return true
}

/**
 * Convert Rails DOM experience row shape to ExperienceInput for computeABGroups.
 * Row: { id, country_code, state, start_at, end_at, devices }
 * devices: string 'all' | 'ios' | 'android' (normalized)
 */
export function toExperienceInput(row) {
  const id = String(row.id || "")
  const countries = Array.isArray(row.country_code)
    ? row.country_code.map(String).filter(Boolean)
    : row.country_code
    ? [String(row.country_code)]
    : []
  const all = countries.length === 0
  const state = row.state
  const region =
    Array.isArray(state) && state.length > 1
      ? `MULTI:${state.join(",")}`
      : Array.isArray(state) && state.length === 1
        ? state[0]
        : state || null

  const location = { all, countries: all ? [] : countries, region }

  let schedule = null
  if (row.start_at && row.end_at) {
    const start = row.start_at.trim()
    const end = row.end_at.trim()
    if (start && end) schedule = { start, end }
  }

  let device = (row.devices || "all").toLowerCase()
  if (device !== "ios" && device !== "android") device = "all"

  return { id, schedule, location, device }
}
